// @ts-nocheck
import { BaseModel, ProductAutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import tryJsonParse from "@store/Framework/Common/Utilities/tryJsonParse";
import { MultiLocationCatalogResultModel } from "@store/Framework/Types/ApiModels";

type MultiLocationCatalogResultMap = Map<string, MultiLocationCatalogResultModel | undefined>;

const getMultiLocationCatalogProductUniqueCustomerNames = (mlcResult?: MultiLocationCatalogResultModel): string[] => {
    if (!mlcResult?.availableAtOtherShipTos) {
        return [];
    }

    const uniqueNames = new Set<string>();

    Object.values(mlcResult.availableAtOtherShipTos).forEach(shipTo => {
        if (shipTo.customerProductName) {
            uniqueNames.add(shipTo.customerProductName);
        }
    });

    return Array.from(uniqueNames);
};

/**
 * Calculates the primary and additional customer product names for a product.
 *
 * @param customerProductNumber - The product to calculate additional customer product names for.
 * @param multiLocationCatalogResult - The product's associated MultiLocationCatalogResultModels.
 * @returns An object containing the primary name and the count of additional names.
 */
export const calculateProductAdditionalCustomerProductNames = (
    customerProductNumber: string | undefined,
    multiLocationCatalogResult: MultiLocationCatalogResultModel | undefined,
) => {
    if (!multiLocationCatalogResult?.isMultiLocationCatalogProduct) {
        return { primary: customerProductNumber, additionalCount: 0 };
    }

    const uniqueNames = getMultiLocationCatalogProductUniqueCustomerNames(multiLocationCatalogResult);
    const isNameCustomerOverride = Boolean(customerProductNumber);
    const primaryName = isNameCustomerOverride ? customerProductNumber! : uniqueNames[0];
    return {
        primary: primaryName,
        additionalCount: uniqueNames.filter(name => name !== primaryName).length,
    };
};

/**
 * Calculates the primary and additional customer product names for a list of AutoComplete products
 *
 * @param products - The products to calculate additional customer product names for.
 * @param multiLocationCatalogResultMap - A map of product IDs to their MultiLocationCatalogResultModels.
 * @returns A map where the key is the product ID and the value is an object containing the primary name and the count of additional names.
 */
export const calculateProductsAdditionalCustomerProductNames = (
    products: ProductAutocompleteItemModel[],
    multiLocationCatalogResultMap: MultiLocationCatalogResultMap,
) => {
    return products.reduce<Map<string, { primary: string | undefined; additionalCount: number }>>((map, product) => {
        if (!product.id) {
            return map;
        }

        const mlcResult = multiLocationCatalogResultMap.get(product.id);
        map.set(product.id, calculateProductAdditionalCustomerProductNames(product.name, mlcResult));

        return map;
    }, new Map());
};

/**
 * Generates a map of product IDs to their MultiLocationCatalogResultModels.
 * @param products - The products to extract MultiLocationCatalogResultModels from.
 * @returns A map where the key is the product ID and the value is the corresponding MultiLocationCatalogResultModel.
 */
export const getProductMultiLocationCatalogResultMap = (
    products: (BaseModel & { id: string | null })[],
): MultiLocationCatalogResultMap => {
    return products.reduce<MultiLocationCatalogResultMap>((map, product) => {
        if (product.id && product.properties.multiLocationCatalogResult) {
            map.set(product.id, getMultiLocationCatalogResult(product));
        }
        return map;
    }, new Map());
};

export const getMultiLocationCatalogResult = ({ properties }: BaseModel) => {
    return tryJsonParse<MultiLocationCatalogResultModel>(properties.multiLocationCatalogResult);
};
